import {
  useCreateUserClientAttributeQuery,
  useDeleteUserClientAttributeQuery,
  useGetAbacAppsQuery,
  useGetAllAttributesForAbacAppQuery,
  useGetUserClientAttributesByClientQuery,
} from "../../../Pages/ABAC/AbacAppQueries";
import { Button, DialogActions, Grid, Typography } from "@mui/material";
import { IApp } from "../../Typings/AbacApp";
import TransferList from "../TransferList/TransferList";
import {
  ITransferListItem,
  ITransferListPanel,
} from "../../Typings/TransferList";
import { useState, useEffect, useCallback } from "react";
import DropdownComponent from "../Dropdown/DropdownComponent";
import { IAttribute } from "../../Typings/AbacAttribute";
import { ICreateUserClientAttribute } from "../../Typings/AbacUserClientAttribute";
import { selectClientValue } from "../../State/Slices/SelectedClientSlice";
import { useAppSelector } from "../../State/Store";
import { useModalHooks } from "../../State/Hooks/useModalHooks";
import { selectAbacUser } from "../../State/Slices/AbacUserSlice";
import { useNotification } from "../../State/Hooks/useNotificationHooks";
import BlockSkeleton from "../Skeleton/BlockSkeleton";

interface IAttributeAssignmentComponentProps {
  handleClose?: any;
  formik?: any;
  submitText?: string;
  clientId?: string;
}

const formikStyles = {
  width: "85%",
  mr: "0%",
  ml: "10%",
  textAlign: "left",
};

const baseStyles = {
  width: "45%",
  mr: "10%",
  ml: "0%",
  textAlign: "center",
};

export default function AttributeAssignmentComponent({
  handleClose,
  formik,
  submitText,
  clientId,
}: IAttributeAssignmentComponentProps) {
  const styles = {
    fadeOut: {
      ...(formik ? formikStyles : baseStyles),
      color: "primary.main",
      opacity: "0",
      transition: "opacity 0.5s",
      fontStyle: "italic",
    },
    fadeIn: {
      ...(formik ? formikStyles : baseStyles),
      color: "primary.main",
      opacity: "0.8",
      transition: "opacity 0.5s",
      fontStyle: "italic",
    },
  };

  const { closeModal } = useModalHooks();
  const { displayError, displaySuccess } = useNotification();

  //Redux State
  const { id: baseClientId } = useAppSelector(selectClientValue);
  const { currentUserId: userId } = useAppSelector(selectAbacUser);

  //Local state
  const [appIndex, setAppIndex] = useState<number>(0);
  const [errorOccured, setErrorOccured] = useState<boolean>(false);
  const [showAppSaveMessage, setShowAppSaveMessage] = useState<boolean>(false);
  const [previousApp, setPreviousApp] = useState<IApp>();
  const [assignedAttributes, setAssignedAttributes] = useState<
    ITransferListItem[]
  >([]);
  const [unassignedAttributes, setUnassignedAttributes] = useState<
    ITransferListItem[]
  >([]);

  //React Query
  const { data: apps = [], isLoading: appsLoading } = useGetAbacAppsQuery();

  var appId = apps[appIndex]?.id;

  const { data: appAttributes = [], isLoading: appAttributesLoading } =
    useGetAllAttributesForAbacAppQuery(appId);

  const { mutate: createNewUCA, isLoading: createIsLoading } =
    useCreateUserClientAttributeQuery(userId, clientId);
  const {
    data: userClientAttributes = [],
    isLoading: userClientAttributesLoading,
  } = useGetUserClientAttributesByClientQuery(userId, clientId ?? baseClientId);
  const { mutate: deleteUCA } = useDeleteUserClientAttributeQuery(userId);

  // Attributes assigned to the user on first time load
  const startingAssignedAttributes = userClientAttributes;

  const rightTransferPanel: ITransferListPanel = {
    title: `Unassigned ${apps[appIndex]?.name}  Permissions`,
    listItems: unassignedAttributes,
    setListItems: setUnassignedAttributes,
    listIsLoading: appAttributesLoading,
    listHasAddItem: false,
  };

  const leftTransferPanel: ITransferListPanel = {
    title: `Assigned ${apps[appIndex]?.name}  Permissions`,
    listItems: assignedAttributes,
    setListItems: setAssignedAttributes,
    listIsLoading: userClientAttributesLoading,
    listHasAddItem: false,
  };

  const handleAppChange = (value: IApp) => {
    createUserClientAttributes(
      assignedAttributes.map((aa) => aa.item),
      true
    );
    if (!errorOccured) {
      setPreviousApp(apps[appIndex]);
      setShowAppSaveMessage(true);
      setTimeout(() => {
        setShowAppSaveMessage(false);
      }, 3000);
    }
    var newIndex = apps.findIndex((app) => app.id === value.id);
    setAppIndex(newIndex);
  };

  const handleFinish = () => {
    //If this component should submit with a formik form, handle that here
    if (formik) {
      formik.handleSubmit();
    }

    //If no attribute was assigned to the user and this is a new user,
    //add one of the unassigned attributes as an inactive attribute,
    //allowing this user to still show up for this client in the user list
    if (
      startingAssignedAttributes.length === 0 &&
      assignedAttributes.length === 0
    ) {
      createUserClientAttributes(
        unassignedAttributes.map((aa) => aa.item).slice(0, 1),
        false
      );
    }
    createUserClientAttributes(
      assignedAttributes.map((aa) => aa.item),
      true
    );

    if (handleClose) {
      handleClose();
    } else {
      closeModal();
    }

    if (!errorOccured) {
      if (!formik) {
        displaySuccess("Successfully updated permissions!");
      }
    } else {
      displayError("Error updating permissions!");
    }
  };

  // Update a User's Assigned Attributes
  const createUserClientAttributes = useCallback(
    function submitUCA(
      newlyAssignedAttributes: IAttribute[],
      isActive: boolean
    ) {
      if (!createIsLoading) {
        newlyAssignedAttributes.forEach((attribute) => {
          let newUCA: ICreateUserClientAttribute = {
            appId: appId,
            baseClientId: clientId ?? baseClientId,
            userId: userId,
            attributeId: attribute.id,
            isActive: isActive,
          };
          createNewUCA(newUCA, {
            onError: () => {
              setErrorOccured(true);
            },
          });
        });

        // Soft delete any Attributes that were unassigned from a user
        var ucasToRemove = startingAssignedAttributes
          .filter((sa) => sa.attribute.appId === appId)
          .filter(
            (x) =>
              !newlyAssignedAttributes
                .map((a) => a.id)
                .some((id) => x.attributeId === id)
          );

        ucasToRemove.forEach((uca) => {
          deleteUCA(uca.id, {
            onError: () => {
              setErrorOccured(true);
            },
          });
        });
      }
    },
    [
      createIsLoading,
      startingAssignedAttributes,
      appId,
      clientId,
      baseClientId,
      userId,
      createNewUCA,
      deleteUCA,
    ]
  );

  // Populate the TransferList item lists
  useEffect(() => {
    if (!appsLoading) {
      let leftList: ITransferListItem[] = [];
      let rightList: ITransferListItem[] = [];

      if (!appAttributesLoading && appAttributes.length > 0) {
        rightList = appAttributes!.map((attribute) => ({
          item: attribute,
          label: attribute.name,
        }));
      }

      if (!userClientAttributesLoading) {
        leftList = userClientAttributes!
          .filter((uca) => uca.isActive && uca.attribute.appId === appId)
          .map((userClientAttribute) => ({
            item: userClientAttribute.attribute,
            label: userClientAttribute.attribute.name,
          }));
      }

      rightList = rightList.filter(
        (i) => !leftList.find((f) => f.item.id === i.item.id)
      );

      setUnassignedAttributes(rightList);
      setAssignedAttributes(leftList);
    }
  }, [
    appAttributes,
    appAttributesLoading,
    appId,
    appIndex,
    apps,
    appsLoading,
    userClientAttributes,
    userClientAttributesLoading,
  ]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item container sm={12}>
          {!appsLoading ? (
            <DropdownComponent
              title={"Select Application: "}
              currValue={apps[appIndex]}
              items={apps}
              property={"name"}
              handleChange={(value: any) => handleAppChange(value)}
              error={null}
              multi={false}
              emptyRow={false}
            />
          ) : (
            <BlockSkeleton />
          )}
        </Grid>
        <Grid item sm={12}>
          <TransferList
            rightPanel={rightTransferPanel}
            leftPanel={leftTransferPanel}
          />
        </Grid>
      </Grid>
      <DialogActions
        sx={{
          mt: "2%",
          pl: 0,
          pr: 0,
        }}
      >
        <Typography sx={showAppSaveMessage ? styles.fadeIn : styles.fadeOut}>
          Saved user's attributes for {previousApp?.name}
        </Typography>
        <Button
          disabled={formik ? !formik.isValid : false}
          sx={{ width: formik ? "15%" : "45%" }}
          variant="contained"
          onClick={() => handleFinish()}
        >
          {submitText ?? "Finish"}
        </Button>
      </DialogActions>
    </>
  );
}
